import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import {
  HiOutlineCheck,
  HiOutlineCalculator,
  HiOutlineFilter,
  HiOutlineCash,
  HiOutlineRefresh,
  HiOutlineX,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi';

/**
 * Date-range presets — same logic as Attendance so the filters feel
 * consistent across the app. Resolved to (start, end) YYYY-MM-DD pairs.
 */
function rangeForPreset(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  // Helper: get the most recent Saturday (week start)
  const getSaturday = (d) => {
    const dow = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diff = (dow + 1) % 7; // Sat→0, Sun→1, ... Fri→6
    const sat = new Date(d);
    sat.setDate(d.getDate() - diff);
    return sat;
  };

  if (preset === 'today') return [fmt(today), fmt(today)];

  if (preset === 'thisWeek') {
    // Saturday → Today
    const start = getSaturday(today);
    return [fmt(start), fmt(today)];
  }

  if (preset === 'lastWeek') {
    // Last Saturday → Last Friday
    const thisSat = getSaturday(today);
    const lastSat = new Date(thisSat);
    lastSat.setDate(thisSat.getDate() - 7);
    const lastFri = new Date(lastSat);
    lastFri.setDate(lastSat.getDate() + 6);
    return [fmt(lastSat), fmt(lastFri)];
  }

  if (preset === 'thisMonth' || preset === 'lastMonth') {
    const d = new Date(today);
    if (preset === 'lastMonth') d.setMonth(d.getMonth() - 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [fmt(start), fmt(end)];
  }

  if (preset === 'last7') {
    const start = new Date(today); start.setDate(start.getDate() - 6);
    return [fmt(start), fmt(today)];
  }

  if (preset === 'last30') {
    const start = new Date(today); start.setDate(start.getDate() - 29);
    return [fmt(start), fmt(today)];
  }

  return [fmt(today), fmt(today)];
}

const RANGE_PRESETS = [
  { value: 'today',     label: 'আজ' },
  { value: 'thisWeek',  label: 'এই সপ্তাহ' },
  { value: 'lastWeek',  label: 'গত সপ্তাহ' },
  { value: 'thisMonth', label: 'এই মাস' },
  { value: 'lastMonth', label: 'গত মাস' },
  { value: 'last7',     label: 'গত ৭ দিন' },
  { value: 'last30',    label: 'গত ৩০ দিন' },
];

const fmtBDT = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;

const Salary = () => {
  const [salaries, setSalaries] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calcForm, setCalcForm] = useState({
    worker_id: '', week_start: '', week_end: '',
  });
  const { t } = useLanguage();

  // ── Filter state ────────────────────────────────────────────────────────
  const [rangePreset, setRangePreset] = useState('thisWeek');
  const today = new Date().toISOString().split('T')[0];
  const initRange = rangeForPreset('thisMonth');
  const [customStart, setCustomStart] = useState(initRange[0]);
  const [customEnd,   setCustomEnd]   = useState(initRange[1]);

  // Worker filter
  const [filterWorkerId, setFilterWorkerId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Bulk select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPaying, setBulkPaying] = useState(false);
  const [bulkCalculating, setBulkCalculating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [rangeStart, rangeEnd] = useMemo(() => {
    if (rangePreset === 'custom') return [customStart, customEnd];
    return rangeForPreset(rangePreset);
  }, [rangePreset, customStart, customEnd]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch whenever filters change. The backend's getSalaries accepts
  // start_week/end_week for range filtering of the salary list.
  useEffect(() => {
    fetchSalaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, filterWorkerId, statusFilter]);

  const fetchData = async () => {
    try {
      const [workersRes] = await Promise.all([API.get('/admin/workers')]);
      setWorkers(workersRes.data.workers);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const params = { start_week: rangeStart, end_week: rangeEnd };
      if (filterWorkerId) params.worker_id = filterWorkerId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await API.get('/admin/salaries', { params });
      setSalaries(data.salaries);
      setSelectedIds(new Set()); // reset selection on filter change
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Single worker calculate ─────────────────────────────────────────────
  const handleCalculate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post('/admin/salaries/calculate', calcForm);
      alert(`${data.message}\n\nউপস্থিত দিন: ${data.breakdown.present_days}\nআধা দিন: ${data.breakdown.half_days}\nমোট: ${fmtBDT(data.breakdown.gross)}\nঅগ্রিম: ${fmtBDT(data.breakdown.advance)}\nনিট: ${fmtBDT(data.breakdown.net)}`);
      fetchSalaries();
    } catch (error) {
      alert(error.response?.data?.message || 'Error');
    }
  };

  // ── Bulk calculate (all workers or selected) ───────────────────────────
  const handleBulkCalculate = async () => {
    const confirmMsg = `এই রেঞ্জের (${rangeStart} → ${rangeEnd}) জন্য ${workers.filter((w) => w.role === 'worker' && w.is_active).length} জন ওয়ার্কারের বেতন হিসাব করবেন?`;
    if (!confirm(confirmMsg)) return;
    setBulkCalculating(true);
    try {
      const { data } = await API.post('/admin/salaries/bulk-calculate', {
        week_start: rangeStart, week_end: rangeEnd,
      });
      setBulkResult({ kind: 'calculate', ...data });
      fetchSalaries();
    } catch (error) {
      alert(error.response?.data?.message || 'Error');
    } finally {
      setBulkCalculating(false);
    }
  };

  // ── Single pay ──────────────────────────────────────────────────────────
  const [payModal, setPayModal] = useState(null); // {salary, amount, note}
  const [paying, setPaying] = useState(false);

  const openPayModal = (s) => {
    const remaining = Number(s.net_amount) - Number(s.amount_paid || 0);
    setPayModal({ salary: s, amount: String(remaining), note: '', error: '' });
  };

  const submitPay = async () => {
    if (!payModal) return;
    const amount = Number(String(payModal.amount).replace(/[, ]/g, ''));
    const remaining = Number(payModal.salary.net_amount) - Number(payModal.salary.amount_paid || 0);
    if (isNaN(amount) || amount < 0) {
      setPayModal({ ...payModal, error: 'সঠিক পরিমাণ দিন।' });
      return;
    }
    if (amount > remaining) {
      setPayModal({ ...payModal, error: `${fmtBDT(remaining)} এর বেশি দেওয়া যাবে না।` });
      return;
    }
    setPaying(true);
    try {
      await API.put(`/admin/salaries/${payModal.salary.id}/pay`, {
        amount_paid: amount,
        note: payModal.note?.trim() || undefined,
      });
      setPayModal(null);
      fetchSalaries();
    } catch (error) {
      setPayModal({ ...payModal, error: error.response?.data?.message || 'সার্ভার ত্রুটি।' });
    } finally {
      setPaying(false);
    }
  };

  // ── Bulk pay (multi-select) ─────────────────────────────────────────────
  const handleBulkPay = async () => {
    if (selectedIds.size === 0) return;
    const selected = salaries.filter((s) => selectedIds.has(s.id) && s.status !== 'paid');
    const totalAmount = selected.reduce((acc, s) => acc + Number(s.net_amount || 0), 0);
    if (!confirm(`${selected.length} জনের মোট ${fmtBDT(totalAmount)} পরিশোধিত হিসাবে চিহ্নিত করবেন?`)) return;
    setBulkPaying(true);
    try {
      const { data } = await API.post('/admin/salaries/bulk-pay', {
        salary_ids: [...selectedIds],
      });
      setBulkResult({ kind: 'pay', ...data });
      fetchSalaries();
    } catch (error) {
      alert(error.response?.data?.message || 'Error');
    } finally {
      setBulkPaying(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    const pending = salaries.filter((s) => s.status !== 'paid');
    if (selectedIds.size === pending.length && pending.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((s) => s.id)));
    }
  };

  const workerOptions = useMemo(
    () => workers.filter((w) => w.role !== 'admin').sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [workers]
  );

  // Summary stats
  const totalPending = salaries.filter((s) => s.status === 'pending').reduce((acc, s) => acc + Number(s.net_amount || 0), 0);
  const totalPartial = salaries.filter((s) => s.status === 'partial').reduce((acc, s) => acc + Number(s.net_amount || 0), 0);
  const totalPaid    = salaries.filter((s) => s.status === 'paid').reduce((acc, s) => acc + Number(s.net_amount || 0), 0);
  const totalAdvance = salaries.reduce((acc, s) => acc + Number(s.total_advance || 0), 0);

  const pendingCount = salaries.filter((s) => s.status === 'pending').length;

  if (loading && salaries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('salary')}
        subtitle={`${rangeStart} → ${rangeEnd}`}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium">বকেয়া (Pending)</p>
              <p className="text-2xl font-bold text-accent-700 dark:text-accent-300 mt-1">{fmtBDT(totalPending)}</p>
            </div>
            <div className="w-11 h-11 rounded-2xl bg-accent-100 dark:bg-accent-900/40 flex items-center justify-center text-2xl">⏳</div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium">আংশিক (Partial)</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{fmtBDT(totalPartial)}</p>
            </div>
            <div className="w-11 h-11 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-2xl">◐</div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium">পরিশোধিত</p>
              <p className="text-2xl font-bold text-brand-700 dark:text-brand-300 mt-1">{fmtBDT(totalPaid)}</p>
            </div>
            <div className="w-11 h-11 rounded-2xl bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center text-2xl">💰</div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider font-medium">অগ্রিম কর্তন</p>
              <p className="text-2xl font-bold text-violet-700 dark:text-violet-300 mt-1">{fmtBDT(totalAdvance)}</p>
            </div>
            <div className="w-11 h-11 rounded-2xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-2xl">💸</div>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <HiOutlineFilter className="w-5 h-5 text-ink-500 dark:text-ink-400" />
          <h2 className="text-sm font-semibold text-ink-900 dark:text-white uppercase tracking-wider">ফিল্টার</h2>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex gap-1.5 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl overflow-x-auto">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setRangePreset(p.value)}
                className={
                  'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ' +
                  (rangePreset === p.value
                    ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                    : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
                }
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRangePreset('custom')}
              className={
                'px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ' +
                (rangePreset === 'custom'
                  ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                  : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
              }
            >
              কাস্টম
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={rangePreset === 'custom' ? '' : 'opacity-60'}>
              <label className="input-label text-xs">শুরু</label>
              <input type="date" value={customStart} disabled={rangePreset !== 'custom'} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)} className="input-field text-sm" />
            </div>
            <div className={rangePreset === 'custom' ? '' : 'opacity-60'}>
              <label className="input-label text-xs">শেষ</label>
              <input type="date" value={customEnd} disabled={rangePreset !== 'custom'} min={customStart || undefined} max={today} onChange={(e) => setCustomEnd(e.target.value)} className="input-field text-sm" />
            </div>
            <div>
              <label className="input-label text-xs">ওয়ার্কার</label>
              <select value={filterWorkerId} onChange={(e) => setFilterWorkerId(e.target.value)} className="input-field text-sm">
                <option value="">সব ({workerOptions.length} জন)</option>
                {workerOptions.map((w) => <option key={w.id} value={w.id}>👷 {w.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1 p-1 bg-ink-100 dark:bg-ink-800 rounded-xl">
              {[
                { v: 'all', label: 'সব' },
                { v: 'pending', label: `বকেয়া (${salaries.filter((s) => s.status === 'pending').length})` },
                { v: 'paid', label: 'পরিশোধিত' },
                { v: 'partial', label: 'আংশিক' },
              ].map((tab) => (
                <button
                  key={tab.v}
                  type="button"
                  onClick={() => setStatusFilter(tab.v)}
                  className={
                    'px-3 py-1 rounded-lg text-xs font-semibold transition-colors ' +
                    (statusFilter === tab.v
                      ? 'bg-white dark:bg-ink-700 text-brand-700 dark:text-brand-300 shadow-sm'
                      : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white')
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-2">
              <span>সক্রিয়: <strong className="text-ink-900 dark:text-white">{rangeStart}</strong> → <strong className="text-ink-900 dark:text-white">{rangeEnd}</strong></span>
              {(rangePreset !== 'thisMonth' || filterWorkerId || statusFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setRangePreset('thisMonth');
                    setFilterWorkerId('');
                    setStatusFilter('all');
                  }}
                  className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline ml-2"
                >
                  ✕ ফিল্টার মুছুন
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="card bg-gradient-to-r from-brand-50 to-accent-50 dark:from-brand-900/20 dark:to-accent-900/20 border-brand-200 dark:border-brand-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <HiOutlineCalculator className="w-5 h-5 text-brand-600" />
            <div>
              <p className="text-sm font-bold text-ink-900 dark:text-white">বাল্ক অ্যাকশন</p>
              <p className="text-xs text-ink-500 dark:text-ink-400">{rangeStart} → {rangeEnd} রেঞ্জের জন্য</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleBulkCalculate}
              disabled={bulkCalculating}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              title={`সব ওয়ার্কারের বেতন হিসাব (অগ্রিম কেটে রাখা হবে)`}
            >
              {bulkCalculating ? <HiOutlineRefresh className="w-4 h-4 animate-spin" /> : <HiOutlineCalculator className="w-4 h-4" />}
              {bulkCalculating ? 'হিসাব হচ্ছে...' : `সবার বেতন হিসাব`}
            </button>
            <button
              type="button"
              onClick={handleBulkPay}
              disabled={bulkPaying || selectedIds.size === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              title="সিলেক্ট করা বেতন একসাথে পরিশোধ করুন"
            >
              {bulkPaying ? <HiOutlineRefresh className="w-4 h-4 animate-spin" /> : <HiOutlineCash className="w-4 h-4" />}
              {bulkPaying ? 'পরিশোধ হচ্ছে...' : `${selectedIds.size} টি পরিশোধ করুন`}
            </button>
            <button
              type="button"
              onClick={() => {
                const token = localStorage.getItem('token');
                const url = `${window.location.origin}/api/admin/export/excel?start_date=${rangeStart}&end_date=${rangeEnd}`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => r.blob())
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `plumber_backup_${new Date().toISOString().split('T')[0]}.xlsx`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  });
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
              title="Excel ফাইলে ব্যাকআপ ডাউনলোড করুন"
            >
              📥 Excel ডাউনলোড
            </button>
          </div>
        </div>
        {bulkResult && (
          <div className="mt-3 p-3 bg-white dark:bg-ink-800 rounded-xl border border-emerald-200 dark:border-emerald-800 text-sm">
            <div className="flex items-start gap-2">
              <HiOutlineCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-800 dark:text-emerald-200">{bulkResult.message}</p>
                {bulkResult.kind === 'calculate' && bulkResult.results && (
                  <div className="mt-2 max-h-48 overflow-y-auto space-y-1 text-xs">
                    {bulkResult.results.slice(0, 10).map((r) => (
                      <div key={r.worker_id} className="flex justify-between gap-3">
                        <span className="text-ink-700 dark:text-ink-300">{r.worker_name}</span>
                        <span className="text-ink-900 dark:text-white font-semibold">
                          {fmtBDT(r.net)}
                          {r.advance > 0 && <span className="text-violet-600 ml-1">(−{fmtBDT(r.advance)} অগ্রিম)</span>}
                        </span>
                      </div>
                    ))}
                    {bulkResult.results.length > 10 && (
                      <p className="text-ink-500">...আরো {bulkResult.results.length - 10} জন</p>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => setBulkResult(null)} className="text-ink-400 hover:text-ink-700"><HiOutlineX className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Single calculate form */}
      <div className="card">
        <h2 className="text-base font-semibold text-ink-900 dark:text-white mb-4 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-brand-500" />
          একক বেতন হিসাব
        </h2>
        <form onSubmit={handleCalculate} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="input-label">ওয়ার্কার</label>
            <select value={calcForm.worker_id} onChange={(e) => setCalcForm({ ...calcForm, worker_id: e.target.value })} className="input-field" required>
              <option value="">— নির্বাচন —</option>
              {workerOptions.map((w) => <option key={w.id} value={w.id}>👷 {w.name}</option>)}
            </select>
          </div>
          <div>
            <label className="input-label">{t('weekStart')}</label>
            <input type="date" value={calcForm.week_start} onChange={(e) => setCalcForm({ ...calcForm, week_start: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="input-label">{t('weekEnd')}</label>
            <input type="date" value={calcForm.week_end} onChange={(e) => setCalcForm({ ...calcForm, week_end: e.target.value })} className="input-field" required />
          </div>
          <div className="sm:col-span-3">
            <button type="submit" className="btn-primary">
              <HiOutlineCalculator className="w-5 h-5" />
              বেতন হিসাব করুন
            </button>
          </div>
        </form>
      </div>

      {/* History table */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-base font-semibold text-ink-900 dark:text-white flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            {t('paymentHistory')}
          </h2>
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
            >
              {selectedIds.size === pendingCount && pendingCount > 0 ? 'সব অচিহ্নিত করুন' : `সব বকেয়া (${pendingCount}) চিহ্নিত করুন`}
            </button>
          )}
        </div>

        {salaries.length === 0 ? (
          <EmptyState icon="💵" title="এই রেঞ্জে কোনো বেতন নেই" description="ফিল্টার বদলে দেখুন অথবা বাল্ক হিসাব করুন" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-ink-50 dark:bg-ink-900/30 border-y border-ink-100 dark:border-ink-700">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={pendingCount > 0 && selectedIds.size === pendingCount}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="সব বকেয়া নির্বাচন করুন"
                    />
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">ওয়ার্কার</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">সপ্তাহ / রেঞ্জ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-center">দিন</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">মোট</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অগ্রিম</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">বকেয়া</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">নিট</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">অবস্থা</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অ্যাকশন</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                {salaries.map((s) => {
                  const isPending = s.status === 'pending' || s.status === 'partial';
                  const isSelected = selectedIds.has(s.id);
                  const hasAdvance = Number(s.total_advance) > 0;
                  return (
                    <tr
                      key={s.id}
                      className={
                        'transition-colors ' +
                        (isSelected
                          ? 'bg-brand-50/50 dark:bg-brand-900/20 '
                          : 'hover:bg-ink-50 dark:hover:bg-ink-900/30 ')
                      }
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          disabled={s.status === 'paid'}
                          checked={isSelected}
                          onChange={() => toggleSelect(s.id)}
                          className="w-4 h-4 rounded text-brand-600 focus:ring-brand-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center text-white font-bold text-xs">
                            {s.worker?.name?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <span className="font-semibold text-ink-900 dark:text-white block truncate">{s.worker?.name}</span>
                            {hasAdvance && (
                              <span className="text-[10px] text-violet-600 dark:text-violet-400 font-semibold">
                                💸 অগ্রিম কর্তন: {fmtBDT(s.total_advance)}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-xs text-ink-600 dark:text-ink-400 whitespace-nowrap">
                        <span className="block">{s.week_start}</span>
                        <span className="block">↓ {s.week_end}</span>
                      </td>
                      <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-center font-medium">{Number(s.total_days).toFixed(1)}</td>
                      <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 text-right">{fmtBDT(s.gross_amount)}</td>
                      <td className={`px-5 py-3 text-sm font-semibold text-right ${hasAdvance ? 'text-violet-600 dark:text-violet-400' : 'text-ink-400'}`}>
                        {hasAdvance ? `−${fmtBDT(s.total_advance)}` : '—'}
                      </td>
                      <td className={`px-5 py-3 text-sm font-semibold text-right ${Number(s.balance_forward) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-ink-400'}`}>
                        {Number(s.balance_forward) > 0 ? `+${fmtBDT(s.balance_forward)}` : '—'}
                      </td>
                      <td className="px-5 py-3 text-sm font-bold text-brand-700 dark:text-brand-300 text-right whitespace-nowrap">{fmtBDT(s.net_amount)}</td>
                      <td className="px-5 py-3">
                        <Badge
                          variant={s.status === 'paid' ? 'success' : s.status === 'partial' ? 'warning' : 'neutral'}
                          dot={s.status === 'pending'}
                        >
                          {s.status === 'paid' ? 'পরিশোধিত'
                            : s.status === 'partial' ? 'আংশিক'
                            : 'বকেয়া'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {isPending && (
                          <button onClick={() => openPayModal(s)} className="btn-success btn-sm">
                            <HiOutlineCheck className="w-4 h-4" />
                            {s.status === 'partial' ? 'বাকি দিন' : 'পরিশোধ'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Beautiful Pay Modal ─────────────────────────────────────── */}
    <Modal
      isOpen={!!payModal}
      onClose={() => !paying && setPayModal(null)}
      size="md"
      title="💰 বেতন পরিশোধ"
    >
      {payModal && (() => {
        const s = payModal.salary;
        const remaining = Number(s.net_amount) - Number(s.amount_paid || 0);
        const balanceFwd = Number(s.balance_forward || 0);
        const advance = Number(s.total_advance || 0);
        const amount = Number(String(payModal.amount).replace(/[, ]/g, '')) || 0;
        const newRemaining = Math.max(0, remaining - amount);
        const willBePaid = amount >= remaining;

        return (
          <div className="space-y-4">
            {/* Worker header */}
            <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-brand-50 to-accent-50 dark:from-brand-900/20 dark:to-accent-900/20 rounded-xl">
              <div className="w-11 h-11 rounded-xl bg-gradient-brand flex items-center justify-center text-white font-bold">
                {s.worker?.name?.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-bold text-ink-900 dark:text-white">{s.worker?.name}</p>
                <p className="text-[11px] text-ink-500 dark:text-ink-400">
                  {s.week_start} → {s.week_end} · {Number(s.total_days).toFixed(1)} দিন
                </p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                s.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
              }`}>
                {s.status === 'partial' ? 'আংশিক' : 'বকেয়া'}
              </span>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2.5 rounded-lg bg-ink-50 dark:bg-ink-900/30">
                <p className="text-[10px] uppercase tracking-wider text-ink-500">মোট</p>
                <p className="font-bold text-sm text-ink-900 dark:text-white mt-1">{fmtBDT(s.gross_amount)}</p>
              </div>
              {balanceFwd > 0 && (
                <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700">বকেয়া</p>
                  <p className="font-bold text-sm text-amber-700 dark:text-amber-300 mt-1">+{fmtBDT(balanceFwd)}</p>
                </div>
              )}
              {advance > 0 && (
                <div className="p-2.5 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                  <p className="text-[10px] uppercase tracking-wider text-violet-700">অগ্রিম</p>
                  <p className="font-bold text-sm text-violet-700 dark:text-violet-300 mt-1">−{fmtBDT(advance)}</p>
                </div>
              )}
              <div className="p-2.5 rounded-lg bg-brand-50 dark:bg-brand-900/20">
                <p className="text-[10px] uppercase tracking-wider text-brand-700">নিট</p>
                <p className="font-bold text-sm text-brand-700 dark:text-brand-300 mt-1">{fmtBDT(s.net_amount)}</p>
              </div>
            </div>

            {Number(s.amount_paid) > 0 && (
              <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-sm">
                <span className="text-emerald-700 dark:text-emerald-300">
                  আগে দেওয়া হয়েছে: <strong>{fmtBDT(s.amount_paid)}</strong>
                </span>
              </div>
            )}

            {/* Amount input */}
            <div>
              <label className="input-label flex justify-between">
                <span>💵 পরিশোধের পরিমাণ</span>
                <button
                  type="button"
                  onClick={() => setPayModal({ ...payModal, amount: String(remaining) })}
                  className="text-xs font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400 normal-case"
                >
                  পুরো ({fmtBDT(remaining)})
                </button>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={payModal.amount}
                  onChange={(e) => setPayModal({ ...payModal, amount: e.target.value, error: '' })}
                  placeholder={`ম্যাক্স ${fmtBDT(remaining)}`}
                  className="input-field text-2xl font-bold pr-12"
                  autoFocus
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 font-bold text-lg">৳</span>
              </div>
              {payModal.error && (
                <p className="text-rose-600 text-xs mt-1.5 flex items-center gap-1">
                  ⚠️ {payModal.error}
                </p>
              )}
            </div>

            {/* Live preview */}
            <div className={`p-3 rounded-xl border-2 transition-colors ${
              willBePaid
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : newRemaining > 0
                ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
                : 'bg-ink-50 border-ink-200 dark:bg-ink-900/30 dark:border-ink-700'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {willBePaid ? '✅ সম্পূর্ণ পরিশোধ' : '⏳ পরে বাকি'}
                </span>
                <span className={`font-bold ${
                  willBePaid ? 'text-emerald-700' : 'text-amber-700'
                }`}>
                  {fmtBDT(newRemaining)}
                </span>
              </div>
              {!willBePaid && newRemaining > 0 && (
                <p className="text-[10px] mt-1 text-amber-700 dark:text-amber-300">
                  বাকি {fmtBDT(newRemaining)} পরের সপ্তাহে বকেয়া যোগ হবে
                </p>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="input-label">📝 নোট (ঐচ্ছিক)</label>
              <input
                type="text"
                value={payModal.note}
                onChange={(e) => setPayModal({ ...payModal, note: e.target.value })}
                placeholder="যেমন: বিকাশে পাঠানো হয়েছে"
                className="input-field"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-ink-100 dark:border-ink-700">
              <button
                type="button"
                onClick={() => setPayModal(null)}
                disabled={paying}
                className="btn-secondary flex-1"
              >
                বাতিল
              </button>
              <button
                type="button"
                onClick={submitPay}
                disabled={paying || amount <= 0}
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
              >
                {paying ? (
                  <><HiOutlineRefresh className="w-4 h-4 animate-spin" /> প্রসেসিং...</>
                ) : (
                  <><HiOutlineCheck className="w-4 h-4" /> পরিশোধ {fmtBDT(amount)}</>
                )}
              </button>
            </div>
          </div>
        );
      })()}
    </Modal>
    </div>
  );
};

export default Salary;
